import cx from 'classnames';
import { Children, isValidElement, cloneElement, forwardRef, type ReactNode, type ReactSVGElement } from 'react';

import { type ForwardRefComponent } from '../../@types/types';
import { type COLORS, type TINTS } from '../../constants';
import Box from '../box';
import { type BoxProps } from '../box/Box';

import * as theme from './theme.css';

export interface IconProps extends Omit<BoxProps, 'children' | 'className'> {
  /** Element wrapped in Icon tags or sent as children prop */
  children?: ReactNode;
  /** Classname for the Icon component */
  className?: string;
  /** Color of the icon */
  color?: (typeof COLORS)[number];
  /** Tint of the icon */
  tint?: (typeof TINTS)[number];
  /** Opacity of the icon */
  opacity?: number;
}

const Icon: ForwardRefComponent<IconProps> = forwardRef<HTMLElement, IconProps>(
  ({ children, className, color = 'teal', tint = 'normal', opacity = 0.84, ...others }, ref) => {
    const classNames = cx(theme[color], theme[tint], className);

    return (
      <Box
        className={classNames}
        alignItems="center"
        data-teamleader-ui="icon"
        display="flex"
        element="span"
        ref={ref}
        {...others}
      >
        {Children.map(children, (child) => {
          // Check if child is an actual React component
          // if so, pass the needed props. If not, just render it.
          if (!isValidElement(child)) {
            return child;
          }

          return cloneElement(child as ReactSVGElement, {
            opacity,
          });
        })}
      </Box>
    );
  },
);

Icon.displayName = 'Icon';

export default Icon;
